/* Sprint 2R-2C — Driver Cage Pickup screen.
 *
 * Driver sees own sealed cages, scans QR (or uses manual fallback with photo).
 * QR scan is preferred — manual fallback requires photo + reason.
 */
import React from "react";
import { driverApi } from "../lib/api";
import { Loader2, Boxes, ScanLine, Camera, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DriverCagePickupPage() {
  const [cages, setCages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [activePickup, setActivePickup] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi().get("/driver/cages/mine");
      setCages(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente bure");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-brand-text2 p-6">
      <Loader2 className="animate-spin" size={18}/> Indlæser bure...
    </div>
  );

  return (
    <div className="space-y-4 p-4 max-w-2xl mx-auto" data-testid="driver-cage-pickup-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
          <Boxes size={22} className="text-brand-red"/> Hent bur
        </h1>
        <p className="text-sm text-brand-text2 mt-1">
          Scan QR-koden på buret før du forlader HUB.
        </p>
      </div>

      {cages.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center" data-testid="driver-cage-empty">
          <Boxes size={48} className="mx-auto text-brand-text2 mb-3"/>
          <h2 className="font-display font-bold text-lg text-brand-dark mb-1">Ingen bure klar</h2>
          <p className="text-sm text-brand-text2">HUB-medarbejder skal forsegle dit bur før du kan hente det.</p>
        </div>
      ) : (
        cages.map((cage) => (
          <CageBlock key={cage.id} cage={cage}
                     activePickup={activePickup === cage.id}
                     onActivate={() => setActivePickup(cage.id)}
                     onCancel={() => setActivePickup(null)}
                     onDone={() => { setActivePickup(null); load(); }}/>
        ))
      )}
    </div>
  );
}

function CageBlock({ cage, activePickup, onActivate, onCancel, onDone }) {
  const [mode, setMode] = React.useState("qr"); // qr | manual
  const scanRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [manualNumber, setManualNumber] = React.useState("");
  const [manualReason, setManualReason] = React.useState("");
  const [photoUrl, setPhotoUrl] = React.useState("");

  if (cage.status === "picked_up_by_driver") {
    return (
      <div className="bg-emerald-50 border border-emerald-200 p-4" data-testid={`driver-cage-done-${cage.cage_number}`}>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="text-emerald-600" size={22}/>
          <div>
            <div className="font-mono font-bold text-emerald-900">{cage.cage_number}</div>
            <div className="text-xs text-emerald-700">Allerede hentet · {cage.expected_count} pakker</div>
          </div>
        </div>
      </div>
    );
  }

  const submitQR = async () => {
    const v = scanRef.current?.value?.trim();
    if (!v) return toast.error("Indtast eller scan QR");
    setBusy(true);
    try {
      const { data } = await driverApi().post(`/driver/cages/${cage.id}/scan-pickup`,
        { scanned_qr: v });
      toast.success(`Bur ${data.cage_number} hentet — ${data.expected_parcels} pakker`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Forkert QR");
    } finally {
      setBusy(false);
    }
  };

  const submitManual = async () => {
    if (!manualNumber || !manualReason || !photoUrl) {
      return toast.error("Bur-nummer, årsag og billede kræves");
    }
    setBusy(true);
    try {
      const { data } = await driverApi().post(`/driver/cages/${cage.id}/manual-pickup`,
        { cage_number: manualNumber.trim().toUpperCase(),
          reason: manualReason, photo_url: photoUrl });
      toast.success(`Manuel pickup gemt — ${data.expected_parcels} pakker · audit-flagget`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual pickup fejlede");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border-2 border-brand p-4" data-testid={`driver-cage-card-${cage.cage_number}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="font-mono font-bold text-xl text-brand-dark">{cage.cage_number}</div>
          <div className="text-xs text-brand-text2">
            {cage.expected_count} pakker · rute {cage.route_number || cage.route_id?.slice(0,8)}
            {cage.hub_zone && <> · Zone <b>{cage.hub_zone}</b></>}
          </div>
        </div>
        <span className="text-[10px] uppercase font-bold bg-blue-100 text-blue-800 px-2 py-1 rounded">{cage.status}</span>
      </div>

      {!activePickup ? (
        <button onClick={onActivate}
                className="w-full px-4 py-3 bg-brand-red hover:bg-red-700 text-white font-semibold inline-flex items-center justify-center gap-2"
                data-testid={`driver-cage-start-pickup-${cage.cage_number}`}>
          <ScanLine size={18}/> Scan QR
        </button>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => setMode("qr")}
                    className={`flex-1 px-3 py-2 text-sm border ${mode==="qr"?"bg-brand-red text-white border-brand-red":"bg-white border-brand"}`}
                    data-testid={`driver-mode-qr-${cage.cage_number}`}>
              <ScanLine size={14} className="inline mr-1"/> QR scan
            </button>
            <button onClick={() => setMode("manual")}
                    className={`flex-1 px-3 py-2 text-sm border ${mode==="manual"?"bg-amber-600 text-white border-amber-600":"bg-white border-brand"}`}
                    data-testid={`driver-mode-manual-${cage.cage_number}`}>
              <AlertTriangle size={14} className="inline mr-1"/> Manuel (fallback)
            </button>
          </div>

          {mode === "qr" ? (
            <>
              <input ref={scanRef}
                     type="text"
                     placeholder="Scan QR (EMA:CAGE:...)"
                     autoFocus
                     onKeyDown={(e) => { if (e.key === "Enter") submitQR(); }}
                     className="w-full px-4 py-3 border-2 border-brand text-base font-mono"
                     data-testid={`driver-qr-input-${cage.cage_number}`}/>
              <button onClick={submitQR} disabled={busy}
                      className="w-full px-4 py-3 bg-brand-red hover:bg-red-700 text-white font-semibold disabled:opacity-50"
                      data-testid={`driver-qr-submit-${cage.cage_number}`}>
                {busy ? <Loader2 className="animate-spin inline" size={16}/> : "Bekræft pickup"}
              </button>
            </>
          ) : (
            <div className="space-y-2 bg-amber-50 border border-amber-200 p-3">
              <div className="text-xs text-amber-800 flex items-start gap-1">
                <AlertTriangle size={14} className="mt-0.5"/>
                Kun til brug hvis QR-koden er ulæselig. Hændelsen markeres som &quot;Manual Cage Verification&quot; og ses af Gatekeeper.
              </div>
              <input type="text" placeholder="Bur-nummer (fx BUR-20260629-001)"
                     value={manualNumber} onChange={(e) => setManualNumber(e.target.value)}
                     className="w-full px-3 py-2 border border-brand text-sm font-mono"
                     data-testid={`driver-manual-number-${cage.cage_number}`}/>
              <input type="text" placeholder="Årsag (QR slidt/væk/forkert print...)"
                     value={manualReason} onChange={(e) => setManualReason(e.target.value)}
                     className="w-full px-3 py-2 border border-brand text-sm"
                     data-testid={`driver-manual-reason-${cage.cage_number}`}/>
              {/* Photo upload: in production wire to /uploads endpoint. For now expects URL */}
              <div>
                <label className="text-xs text-amber-800 flex items-center gap-1 mb-1">
                  <Camera size={12}/> Billede af bur-label *
                </label>
                <input type="text" placeholder="Billede URL (uploades via app)"
                       value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)}
                       className="w-full px-3 py-2 border border-brand text-sm"
                       data-testid={`driver-manual-photo-${cage.cage_number}`}/>
              </div>
              <button onClick={submitManual} disabled={busy}
                      className="w-full px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50"
                      data-testid={`driver-manual-submit-${cage.cage_number}`}>
                {busy ? <Loader2 className="animate-spin inline" size={16}/> : "Bekræft manuel pickup"}
              </button>
            </div>
          )}

          <button onClick={onCancel} className="w-full text-xs text-brand-text2 hover:underline">
            Annuller
          </button>
        </div>
      )}
    </div>
  );
}
